import * as React from 'react';

import { graphql } from '@apollo/client/react/hoc';
import AllPanelVanityStatsQuery from '@graphql/queries/AllPanelVanityStatsQuery';
import { Card, CardContent, CardHeader, Grid } from '@mui/material';
import startOfISOWeek from 'date-fns/startOfISOWeek';
import startOfMonth from 'date-fns/startOfMonth';
import startOfYear from 'date-fns/startOfYear';
import { get } from 'lodash';
import { flowRight as compose } from 'lodash';

import styles from './PanelList.module.css';

interface Props {
  producerId: number;
  allPanels: any;
  yearPanels: any;
  monthPanels: any;
  weekPanels: any;
}

const PanelVanityStats: React.FunctionComponent<Props> = ({
  allPanels,
  yearPanels,
  monthPanels,
  weekPanels,
}) => {
  const renderCard = (title, stats, size: any = 3) => (
    <Grid item sm={size}>
      <Card>
        <CardHeader
          title={title}
          titleTypographyProps={{ variant: 'subtitle1' }}
        />
        <CardContent style={{ display: 'flex', flexDirection: 'column' }}>
          <span className={styles.panelTitle} style={{ textAlign: 'center' }}>
            {stats}
          </span>
        </CardContent>
      </Card>
    </Grid>
  );

  const computeAverageProducts = () => {
    const totalPanels = get(allPanels, 'allPanels.totalCount');
    const totalProducts = get(allPanels, 'allPanels.nodes')
      .map((i) => i.products.totalCount)
      .reduce((a, b) => a + b, 0);

    return totalProducts / totalPanels || 0;
  };

  const computeAverageReviews = () => {
    const totalPanels = get(allPanels, 'allPanels.totalCount');
    const totalReviews = get(allPanels, 'allPanels.nodes')
      .map((i) => i.reviews.totalCount)
      .reduce((a, b) => a + b, 0);

    return totalReviews / totalPanels || 0;
  };

  if (
    allPanels.loading ||
    yearPanels.loading ||
    monthPanels.loading ||
    weekPanels.loading
  ) {
    return <div />;
  }

  return (
    <div style={{ margin: '20px 0' }}>
      <Grid container spacing={3} direction="row">
        {renderCard('All Panels', get(allPanels, 'allPanels.totalCount'))}
        {renderCard(
          'Panels This Year',
          get(yearPanels, 'allPanels.totalCount'),
        )}
        {renderCard(
          'Panels This Month',
          get(monthPanels, 'allPanels.totalCount'),
        )}
        {renderCard(
          'Panels This Week',
          get(weekPanels, 'allPanels.totalCount'),
        )}
        {renderCard(
          'Average Number of Products',
          computeAverageProducts().toFixed(0),
          6,
        )}
        {renderCard(
          'Average Number of Reviews',
          computeAverageReviews().toFixed(0),
          6,
        )}
      </Grid>
    </div>
  );
};

export default compose(
  graphql(AllPanelVanityStatsQuery, {
    options: ({ producerId }: any) => ({
      variables: { producerId, skipPanelInfo: false },
    }),
    name: 'allPanels',
  }),
  graphql(AllPanelVanityStatsQuery, {
    options: ({ producerId }: any) => ({
      variables: {
        producerId,
        filter: {
          endTime: {
            greaterThanOrEqualTo: startOfYear(new Date()).toISOString(),
          },
        },
      },
    }),
    name: 'yearPanels',
  }),
  graphql(AllPanelVanityStatsQuery, {
    options: ({ producerId }: any) => ({
      variables: {
        producerId,
        filter: {
          endTime: {
            greaterThanOrEqualTo: startOfMonth(new Date()).toISOString(),
          },
        },
      },
    }),
    name: 'monthPanels',
  }),
  graphql(AllPanelVanityStatsQuery, {
    options: ({ producerId }: any) => ({
      variables: {
        producerId,
        filter: {
          endTime: {
            greaterThanOrEqualTo: startOfISOWeek(new Date()).toISOString(),
          },
        },
      },
    }),
    name: 'weekPanels',
  }),
)(PanelVanityStats);
